import 'prismjs/themes/prism-tomorrow.css';

import { createFileRoute } from '@tanstack/react-router';
import { highlight, languages } from 'prismjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Editor from 'react-simple-code-editor';
import { toast } from 'sonner';
import { useDebounceCallback } from 'usehooks-ts';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { LoadingIcon, LoadingWrapper } from '@/components/ui/loading-indicator';
import { updateQtiStimuli } from '@/features/admin/actions/update-qti-stimuli';
import { ActionGuard } from '@/features/admin/components/ActionGuard';
import { AdminPageContent } from '@/features/admin/components/AdminPageContent';
import {
  AdminPageHeader,
  BreadcrumbItemProps,
} from '@/features/admin/components/AdminPageHeader';
import { qtiApi } from '@/lib/http.server';

import QtiRenderer from '../../components/qti/QtiRenderer';

export const Route = createFileRoute('/admin/qti-stimulus-render')({
  component: RouteComponent,
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      identifier: (search.identifier as string) ?? '',
    };
  },
});

type QtiStimulus = {
  identifier: string;
  rawXml: string;
  title: string;
};

function RouteComponent() {
  const { identifier } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [stimulusIdentifier, setStimulusIdentifier] = useState(identifier);
  const [stimulus, setStimulus] = useState<QtiStimulus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (stimulusIdentifier) {
      fetchStimuli();
    }
  }, []);

  const handleStimulusIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStimulusIdentifier = e.target.value;
    setStimulusIdentifier(newStimulusIdentifier);
    debouncedSearchSync(newStimulusIdentifier);
  };

  const debouncedSearchSync = useDebounceCallback(
    (newStimulusIdentifier: string) => {
      navigate({
        search: { identifier: newStimulusIdentifier },
      });
    },
    500
  );

  const fetchStimuli = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await qtiApi.get(`/stimuli/${identifier}`);
      const data = response.data;

      setStimulus(data);
    } catch (error) {
      console.error('Error fetching stimulus:', error);
      setError(
        new Error('There was an error loading the stimulus, please try again.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateStimulus = async (
    stimulusId: string,
    title: string,
    rawXml: string
  ) => {
    try {
      await updateQtiStimuli({
        data: {
          stimulusId,
          title,
          rawXml,
        },
      });

      toast.success('Stimulus updated successfully');
    } catch (error) {
      console.error('Error updating stimulus:', error);
      toast.error(
        'Error updating stimulus: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  };

  const breadcrumbItems: BreadcrumbItemProps[] = [
    {
      label: 'Admin',
      link: {
        to: '/admin',
      },
    },
    {
      label: 'QTI Stimulus Renderer',
      link: {
        to: '/admin/qti-stimulus-render',
      },
    },
  ];

  return (
    <AdminPageContent>
      <AdminPageHeader breadcrumbItems={breadcrumbItems} />
      <div className="container mx-auto p-4">
        <h1 className="text-3xl font-bold">QTI Stimulus Renderer</h1>

        <div className="flex gap-4 mt-4 mb-4">
          <Input
            type="text"
            value={stimulusIdentifier}
            onChange={handleStimulusIdentifierChange}
            placeholder="Enter QTI Stimulus Identifier"
            className="flex-1"
          />
          <div className="flex flex-col gap-2">
            <Button onClick={fetchStimuli} disabled={isLoading}>
              Load Stimulus
            </Button>
          </div>
        </div>

        <div className="relative">
          {error ? (
            <>
              <div className="mt-8 flex flex-col gap-2 items-center">
                <p className="text-red-500">{error.message}</p>
              </div>
            </>
          ) : isLoading ? (
            <LoadingWrapper className="mt-8">
              <LoadingIcon />
            </LoadingWrapper>
          ) : (
            <>
              {stimulus && (
                <>
                  <QtiStimulusRenderer
                    stimulus={stimulus}
                    onUpdate={updateStimulus}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AdminPageContent>
  );
}

type QtiStimulusRendererProps = {
  stimulus: QtiStimulus;
  onUpdate: (stimulusId: string, title: string, rawXml: string) => void;
};

function QtiStimulusRenderer({ stimulus, onUpdate }: QtiStimulusRendererProps) {
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      rawXml: stimulus.rawXml,
    },
  });

  const handleSave = (data: { rawXml: string }) => {
    return onUpdate(stimulus.identifier, stimulus.title, data.rawXml);
  };

  const rawXml = watch('rawXml');

  return (
    <div className="border rounded-lg p-4 shadow-sm bg-white overflow-x-auto">
      <div className="mb-4 text-sm text-gray-600 font-mono flex flex-col justify-between items-start">
        <div className="flex justify-between items-end mb-2 w-full">
          <span />
          <ActionGuard>
            <Button
              variant="ghost"
              size="sm"
              className="text-green-500 hover:text-green-700 hover:bg-green-50"
              onClick={handleSubmit(handleSave)}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </ActionGuard>
        </div>
        <ActionGuard>
          <Collapsible className="p-2 border rounded-md mb-4 w-full">
            <CollapsibleTrigger className="cursor-pointer flex items-center justify-between w-full">
              Raw XML Editor
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Editor
                value={rawXml}
                onValueChange={(value) => {
                  setValue('rawXml', value ?? '');
                }}
                highlight={(code) => highlight(code, languages.xml!, 'xml')}
                padding={10}
                autoFocus={false}
                style={{
                  fontFamily: '"Fira code", "Fira Mono", monospace',
                  fontSize: 12,
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  marginBottom: '10px',
                }}
              />
            </CollapsibleContent>
          </Collapsible>
        </ActionGuard>
      </div>
      <QtiRenderer xml={rawXml} isDebugMode />
    </div>
  );
}
